import { ReactNode } from "react";
import {
  ShoppingCart,
  Star,
  Package,
  CreditCard,
  Calendar,
  Clock,
  Phone,
  MapPin,
  Heart,
  Activity,
  User,
  Users,
  Trophy,
  Zap,
  ChevronRight,
  BookOpen,
  GraduationCap,
  Award,
  Target,
  DollarSign,
  TrendingUp,
  Shield,
  Building2,
  Dumbbell,
  Scale,
  Gavel,
  Camera,
  Plane,
  Map,
  Utensils,
  Chef
} from "lucide-react";

export interface ExampleConfig {
  backgroundColor: string;
  chatButtonColor: string;
  chatButtonIcon: string;
  header: ReactNode;
  hero?: ReactNode;
  content: ReactNode;
}

export const ecommerceConfig: ExampleConfig = {
  backgroundColor: "white",
  chatButtonColor: "blue-600",
  chatButtonIcon: "💬",
  header: (
    <header className="bg-gray-900 text-white">
      <div className="container mx-auto px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-8">
            <h1 className="text-2xl font-bold">TechStore</h1>
            <nav className="hidden md:flex space-x-6">
              <a href="#" className="hover:text-gray-300">Products</a>
              <a href="#" className="hover:text-gray-300">Deals</a>
              <a href="#" className="hover:text-gray-300">Support</a>
            </nav>
          </div>
          <div className="flex items-center space-x-4">
            <ShoppingCart className="w-6 h-6" />
            <span className="bg-red-500 text-xs px-2 py-1 rounded-full">3</span>
          </div>
        </div>
      </div>
    </header>
  ),
  hero: (
    <section className="bg-gradient-to-r from-blue-600 to-purple-600 text-white py-20">
      <div className="container mx-auto px-4 text-center">
        <h2 className="text-5xl font-bold mb-4">Black Friday Sale</h2>
        <p className="text-xl mb-8">Up to 50% off on all electronics</p>
        <button className="bg-white text-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100">
          Shop Now
        </button>
      </div>
    </section>
  ),
  content: (
    <>
      {/* Product Grid */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <h3 className="text-3xl font-bold text-center mb-12">Featured Products</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { name: "MacBook Pro M2", price: "$1,999", rating: 4.8, image: "💻" },
              { name: "iPhone 14 Pro", price: "$999", rating: 4.9, image: "📱" },
              { name: "AirPods Pro", price: "$249", rating: 4.7, image: "🎧" }
            ].map((product, index) => (
              <div key={index} className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow">
                <div className="h-48 bg-gray-100 flex items-center justify-center text-6xl">
                  {product.image}
                </div>
                <div className="p-6">
                  <h4 className="text-xl font-semibold mb-2">{product.name}</h4>
                  <div className="flex items-center mb-2">
                    <div className="flex text-yellow-400">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} className="w-4 h-4 fill-current" />
                      ))}
                    </div>
                    <span className="ml-2 text-gray-600">({product.rating})</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-2xl font-bold text-blue-600">{product.price}</span>
                    <button className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700">
                      Add to Cart
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust Indicators */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div className="flex flex-col items-center">
              <Package className="w-12 h-12 text-blue-600 mb-2" />
              <h4 className="font-semibold">Free Shipping</h4>
              <p className="text-gray-600">On orders over $50</p>
            </div>
            <div className="flex flex-col items-center">
              <CreditCard className="w-12 h-12 text-blue-600 mb-2" />
              <h4 className="font-semibold">Secure Payment</h4>
              <p className="text-gray-600">SSL encrypted</p>
            </div>
            <div className="flex flex-col items-center">
              <Clock className="w-12 h-12 text-blue-600 mb-2" />
              <h4 className="font-semibold">24/7 Support</h4>
              <p className="text-gray-600">Always here to help</p>
            </div>
            <div className="flex flex-col items-center">
              <Star className="w-12 h-12 text-blue-600 mb-2" />
              <h4 className="font-semibold">5-Star Reviews</h4>
              <p className="text-gray-600">From 10k+ customers</p>
            </div>
          </div>
        </div>
      </section>
    </>
  )
};

export const healthcareConfig: ExampleConfig = {
  backgroundColor: "blue-50",
  chatButtonColor: "blue-600",
  chatButtonIcon: "🏥",
  header: (
    <header className="bg-white shadow-sm">
      <div className="container mx-auto px-4 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Heart className="w-8 h-8 text-red-500" />
            <h1 className="text-2xl font-bold text-gray-800">MediCare Plus</h1>
          </div>
          <nav className="flex space-x-6">
            <a href="#" className="text-gray-600 hover:text-blue-600">Find a Doctor</a>
            <a href="#" className="text-gray-600 hover:text-blue-600">Services</a>
            <a href="#" className="text-gray-600 hover:text-blue-600">Patient Portal</a>
            <button className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700">
              Book Appointment
            </button>
          </nav>
        </div>
      </div>
    </header>
  ),
  hero: (
    <section className="bg-gradient-to-br from-blue-600 to-cyan-500 text-white py-16">
      <div className="container mx-auto px-4">
        <div className="max-w-3xl">
          <h2 className="text-4xl font-bold mb-4">Your Health, Our Priority</h2>
          <p className="text-xl mb-8">
            Book appointments, consult with doctors, and manage your health records all in one place.
          </p>
          <div className="flex space-x-4">
            <button className="bg-white text-blue-600 px-6 py-3 rounded-lg font-semibold hover:bg-gray-100">
              Schedule Visit
            </button>
            <button className="border-2 border-white text-white px-6 py-3 rounded-lg hover:bg-white hover:text-blue-600">
              Find Doctor
            </button>
          </div>
        </div>
      </div>
    </section>
  ),
  content: (
    <>
      {/* Services */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <h3 className="text-3xl font-bold text-center mb-12 text-gray-800">Our Services</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { title: "Emergency Care", icon: "🚨", description: "24/7 emergency medical services" },
              { title: "General Checkups", icon: "🩺", description: "Routine health examinations" },
              { title: "Specialist Care", icon: "👨‍⚕️", description: "Expert specialist consultations" }
            ].map((service, index) => (
              <div key={index} className="bg-white rounded-lg shadow-md p-6 text-center hover:shadow-lg transition-shadow">
                <div className="text-5xl mb-4">{service.icon}</div>
                <h4 className="text-xl font-semibold mb-2 text-gray-800">{service.title}</h4>
                <p className="text-gray-600">{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Quick Actions */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="text-center">
              <Calendar className="w-12 h-12 text-blue-600 mx-auto mb-2" />
              <h4 className="font-semibold">Book Appointment</h4>
            </div>
            <div className="text-center">
              <User className="w-12 h-12 text-blue-600 mx-auto mb-2" />
              <h4 className="font-semibold">Find Doctor</h4>
            </div>
            <div className="text-center">
              <Phone className="w-12 h-12 text-blue-600 mx-auto mb-2" />
              <h4 className="font-semibold">Telemedicine</h4>
            </div>
            <div className="text-center">
              <MapPin className="w-12 h-12 text-blue-600 mx-auto mb-2" />
              <h4 className="font-semibold">Locations</h4>
            </div>
          </div>
        </div>
      </section>
    </>
  )
};

export const fitnessConfig: ExampleConfig = {
  backgroundColor: "gray-50",
  chatButtonColor: "orange-500",
  chatButtonIcon: "💪",
  header: (
    <header className="bg-white shadow-sm sticky top-0 z-40">
      <div className="container mx-auto px-4 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-red-500 rounded-lg flex items-center justify-center">
              <Activity className="w-6 h-6 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-gray-800">FitLife Studio</h1>
          </div>
          <nav className="flex items-center space-x-6">
            <a href="#" className="text-gray-600 hover:text-orange-600">Classes</a>
            <a href="#" className="text-gray-600 hover:text-orange-600">Trainers</a>
            <a href="#" className="text-gray-600 hover:text-orange-600">Membership</a>
            <a href="#" className="text-gray-600 hover:text-orange-600">Schedule</a>
            <button className="bg-gradient-to-r from-orange-500 to-red-500 text-white px-4 py-2 rounded-lg hover:shadow-lg transition-shadow">
              Book Class
            </button>
          </nav>
        </div>
      </div>
    </header>
  ),
  hero: (
    <section className="bg-gradient-to-br from-orange-500 via-red-500 to-pink-500 text-white py-20">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-5xl font-bold mb-6">Transform Your Body, Transform Your Life</h2>
          <p className="text-xl mb-8 opacity-90">
            Join our community of fitness enthusiasts and achieve your goals with expert guidance.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="bg-white text-orange-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors">
              Start Free Trial
            </button>
            <button className="border-2 border-white text-white px-8 py-3 rounded-lg hover:bg-white hover:text-orange-600 transition-colors">
              View Classes
            </button>
          </div>
        </div>
      </div>
    </section>
  ),
  content: (
    <>
      {/* Class Schedule */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <h3 className="text-3xl font-bold text-center mb-12">Today's Classes</h3>
          <div className="grid gap-4 max-w-4xl mx-auto">
            {[
              { name: 'HIIT Blast', time: '6:00 AM', duration: '45 min', instructor: 'Mike R.', spots: 3, level: 'Advanced', icon: '🔥' },
              { name: 'Yoga Flow', time: '7:30 AM', duration: '60 min', instructor: 'Sarah L.', spots: 8, level: 'All Levels', icon: '🧘' },
              { name: 'Spin Class', time: '9:00 AM', duration: '50 min', instructor: 'Alex T.', spots: 5, level: 'Intermediate', icon: '🚴' },
              { name: 'Strength Training', time: '5:30 PM', duration: '60 min', instructor: 'John D.', spots: 2, level: 'Intermediate', icon: '💪' }
            ].map((classItem, index) => (
              <div key={index} className="bg-white rounded-lg shadow-md p-6 flex items-center justify-between hover:shadow-lg transition-shadow">
                <div className="flex items-center space-x-4">
                  <div className="text-3xl">{classItem.icon}</div>
                  <div>
                    <h4 className="text-xl font-semibold text-gray-800">{classItem.name}</h4>
                    <p className="text-gray-600">{classItem.time} • {classItem.duration} • {classItem.instructor}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-4">
                  <div className="text-right">
                    <div className="text-sm text-gray-500">{classItem.level}</div>
                    <div className="text-sm text-green-600">{classItem.spots} spots left</div>
                  </div>
                  <button className="bg-gradient-to-r from-orange-500 to-red-500 text-white px-6 py-2 rounded-lg hover:shadow-md transition-shadow">
                    Book Now
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <Trophy className="w-12 h-12 text-orange-500 mx-auto mb-2" />
              <div className="text-3xl font-bold text-gray-800">500+</div>
              <div className="text-gray-600">Members</div>
            </div>
            <div>
              <Users className="w-12 h-12 text-orange-500 mx-auto mb-2" />
              <div className="text-3xl font-bold text-gray-800">25+</div>
              <div className="text-gray-600">Trainers</div>
            </div>
            <div>
              <Calendar className="w-12 h-12 text-orange-500 mx-auto mb-2" />
              <div className="text-3xl font-bold text-gray-800">50+</div>
              <div className="text-gray-600">Classes/Week</div>
            </div>
            <div>
              <Heart className="w-12 h-12 text-orange-500 mx-auto mb-2" />
              <div className="text-3xl font-bold text-gray-800">98%</div>
              <div className="text-gray-600">Satisfaction</div>
            </div>
          </div>
        </div>
      </section>
    </>
  )
};

// Additional configs for other examples would go here...
// For brevity, I'll create a few more key ones

export const educationConfig: ExampleConfig = {
  backgroundColor: "blue-50",
  chatButtonColor: "blue-600",
  chatButtonIcon: "📚",
  header: (
    <header className="bg-white shadow-sm">
      <div className="container mx-auto px-4 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <GraduationCap className="w-8 h-8 text-blue-600" />
            <h1 className="text-2xl font-bold text-gray-800">EduTech Academy</h1>
          </div>
          <nav className="flex space-x-6">
            <a href="#" className="text-gray-600 hover:text-blue-600">Courses</a>
            <a href="#" className="text-gray-600 hover:text-blue-600">Programs</a>
            <a href="#" className="text-gray-600 hover:text-blue-600">Resources</a>
            <button className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700">
              Enroll Now
            </button>
          </nav>
        </div>
      </div>
    </header>
  ),
  hero: (
    <section className="bg-gradient-to-br from-blue-600 to-indigo-700 text-white py-20">
      <div className="container mx-auto px-4 text-center">
        <h2 className="text-5xl font-bold mb-6">Learn Without Limits</h2>
        <p className="text-xl mb-8 max-w-2xl mx-auto">
          Access world-class education from anywhere. Build skills that matter in today's digital world.
        </p>
        <button className="bg-white text-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100">
          Start Learning
        </button>
      </div>
    </section>
  ),
  content: (
    <section className="py-16">
      <div className="container mx-auto px-4">
        <h3 className="text-3xl font-bold text-center mb-12">Popular Courses</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {[
            { title: "Web Development", students: "12.5k", rating: 4.8, icon: "💻" },
            { title: "Data Science", students: "8.2k", rating: 4.9, icon: "📊" },
            { title: "Digital Marketing", students: "15.1k", rating: 4.7, icon: "📈" }
          ].map((course, index) => (
            <div key={index} className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow">
              <div className="h-32 bg-gradient-to-r from-blue-500 to-purple-600 flex items-center justify-center text-4xl text-white">
                {course.icon}
              </div>
              <div className="p-6">
                <h4 className="text-xl font-semibold mb-2">{course.title}</h4>
                <p className="text-gray-600 mb-4">{course.students} students enrolled</p>
                <div className="flex items-center justify-between">
                  <span className="text-yellow-500">★ {course.rating}</span>
                  <button className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700">
                    Enroll
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
};

export const financialConfig: ExampleConfig = {
  backgroundColor: "gray-50",
  chatButtonColor: "green-600",
  chatButtonIcon: "💰",
  header: (
    <header className="bg-white shadow-sm">
      <div className="container mx-auto px-4 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <TrendingUp className="w-8 h-8 text-green-600" />
            <h1 className="text-2xl font-bold text-gray-800">WealthWise Financial</h1>
          </div>
          <nav className="flex space-x-6">
            <a href="#" className="text-gray-600 hover:text-green-600">Investments</a>
            <a href="#" className="text-gray-600 hover:text-green-600">Planning</a>
            <a href="#" className="text-gray-600 hover:text-green-600">Advice</a>
            <button className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700">
              Get Started
            </button>
          </nav>
        </div>
      </div>
    </header>
  ),
  hero: (
    <section className="bg-gradient-to-br from-green-600 to-blue-600 text-white py-20">
      <div className="container mx-auto px-4 text-center">
        <h2 className="text-5xl font-bold mb-6">Secure Your Financial Future</h2>
        <p className="text-xl mb-8 max-w-3xl mx-auto">
          Expert financial planning and investment management to help you achieve your goals.
        </p>
        <button className="bg-white text-green-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100">
          Start Planning
        </button>
      </div>
    </section>
  ),
  content: (
    <section className="py-16">
      <div className="container mx-auto px-4">
        <h3 className="text-3xl font-bold text-center mb-12">Our Services</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {[
            { title: "Investment Planning", icon: <TrendingUp className="w-12 h-12 text-green-600" />, description: "Grow your wealth with smart investments" },
            { title: "Retirement Planning", icon: <Shield className="w-12 h-12 text-green-600" />, description: "Secure your retirement future" },
            { title: "Tax Optimization", icon: <DollarSign className="w-12 h-12 text-green-600" />, description: "Minimize taxes, maximize savings" }
          ].map((service, index) => (
            <div key={index} className="bg-white rounded-lg shadow-md p-6 text-center hover:shadow-lg transition-shadow">
              <div className="mb-4 flex justify-center">{service.icon}</div>
              <h4 className="text-xl font-semibold mb-2">{service.title}</h4>
              <p className="text-gray-600">{service.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
};

export const legalConfig: ExampleConfig = {
  backgroundColor: "gray-50",
  chatButtonColor: "gray-800",
  chatButtonIcon: "⚖️",
  header: (
    <header className="bg-white shadow-sm">
      <div className="container mx-auto px-4 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Gavel className="w-8 h-8 text-gray-800" />
            <h1 className="text-2xl font-bold text-gray-800">LawFirm Partners</h1>
          </div>
          <nav className="flex space-x-6">
            <a href="#" className="text-gray-600 hover:text-gray-800">Practice Areas</a>
            <a href="#" className="text-gray-600 hover:text-gray-800">About</a>
            <a href="#" className="text-gray-600 hover:text-gray-800">Contact</a>
            <button className="bg-gray-800 text-white px-4 py-2 rounded-lg hover:bg-gray-900">
              Consultation
            </button>
          </nav>
        </div>
      </div>
    </header>
  ),
  hero: (
    <section className="bg-gradient-to-br from-gray-800 to-gray-600 text-white py-20">
      <div className="container mx-auto px-4 text-center">
        <h2 className="text-5xl font-bold mb-6">Expert Legal Counsel</h2>
        <p className="text-xl mb-8 max-w-3xl mx-auto">
          Experienced attorneys providing comprehensive legal services for individuals and businesses.
        </p>
        <button className="bg-white text-gray-800 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100">
          Free Consultation
        </button>
      </div>
    </section>
  ),
  content: (
    <section className="py-16">
      <div className="container mx-auto px-4">
        <h3 className="text-3xl font-bold text-center mb-12">Practice Areas</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {[
            { title: "Corporate Law", icon: <Building2 className="w-12 h-12 text-gray-800" />, description: "Business formation and corporate governance" },
            { title: "Family Law", icon: <Heart className="w-12 h-12 text-gray-800" />, description: "Divorce, custody, and family matters" },
            { title: "Criminal Defense", icon: <Shield className="w-12 h-12 text-gray-800" />, description: "Protecting your rights in criminal cases" }
          ].map((service, index) => (
            <div key={index} className="bg-white rounded-lg shadow-md p-6 text-center hover:shadow-lg transition-shadow">
              <div className="mb-4 flex justify-center">{service.icon}</div>
              <h4 className="text-xl font-semibold mb-2">{service.title}</h4>
              <p className="text-gray-600">{service.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
};

export const realEstateConfig: ExampleConfig = {
  backgroundColor: "blue-50",
  chatButtonColor: "blue-600",
  chatButtonIcon: "🏠",
  header: (
    <header className="bg-white shadow-sm">
      <div className="container mx-auto px-4 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Building2 className="w-8 h-8 text-blue-600" />
            <h1 className="text-2xl font-bold text-gray-800">Premier Realty</h1>
          </div>
          <nav className="flex space-x-6">
            <a href="#" className="text-gray-600 hover:text-blue-600">Buy</a>
            <a href="#" className="text-gray-600 hover:text-blue-600">Sell</a>
            <a href="#" className="text-gray-600 hover:text-blue-600">Rent</a>
            <button className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700">
              Contact Agent
            </button>
          </nav>
        </div>
      </div>
    </header>
  ),
  hero: (
    <section className="bg-gradient-to-br from-blue-600 to-indigo-700 text-white py-20">
      <div className="container mx-auto px-4 text-center">
        <h2 className="text-5xl font-bold mb-6">Find Your Dream Home</h2>
        <p className="text-xl mb-8 max-w-3xl mx-auto">
          Expert real estate services to help you buy, sell, or rent properties with confidence.
        </p>
        <button className="bg-white text-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100">
          Search Properties
        </button>
      </div>
    </section>
  ),
  content: (
    <section className="py-16">
      <div className="container mx-auto px-4">
        <h3 className="text-3xl font-bold text-center mb-12">Featured Properties</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {[
            { title: "Modern Downtown Condo", price: "$750,000", location: "Downtown", icon: "🏢" },
            { title: "Suburban Family Home", price: "$650,000", location: "Suburbs", icon: "🏡" },
            { title: "Luxury Penthouse", price: "$1,200,000", location: "Uptown", icon: "🏙️" }
          ].map((property, index) => (
            <div key={index} className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow">
              <div className="h-32 bg-gradient-to-r from-blue-500 to-purple-600 flex items-center justify-center text-4xl text-white">
                {property.icon}
              </div>
              <div className="p-6">
                <h4 className="text-xl font-semibold mb-2">{property.title}</h4>
                <p className="text-gray-600 mb-4">{property.location}</p>
                <div className="flex items-center justify-between">
                  <span className="text-2xl font-bold text-blue-600">{property.price}</span>
                  <button className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700">
                    View Details
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
};

export const restaurantConfig: ExampleConfig = {
  backgroundColor: "orange-50",
  chatButtonColor: "orange-600",
  chatButtonIcon: "🍽️",
  header: (
    <header className="bg-white shadow-sm">
      <div className="container mx-auto px-4 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Utensils className="w-8 h-8 text-orange-600" />
            <h1 className="text-2xl font-bold text-gray-800">Bella Vista</h1>
          </div>
          <nav className="flex space-x-6">
            <a href="#" className="text-gray-600 hover:text-orange-600">Menu</a>
            <a href="#" className="text-gray-600 hover:text-orange-600">Reservations</a>
            <a href="#" className="text-gray-600 hover:text-orange-600">Events</a>
            <button className="bg-orange-600 text-white px-4 py-2 rounded-lg hover:bg-orange-700">
              Order Online
            </button>
          </nav>
        </div>
      </div>
    </header>
  ),
  hero: (
    <section className="bg-gradient-to-br from-orange-600 to-red-600 text-white py-20">
      <div className="container mx-auto px-4 text-center">
        <h2 className="text-5xl font-bold mb-6">Authentic Italian Cuisine</h2>
        <p className="text-xl mb-8 max-w-3xl mx-auto">
          Experience the finest Italian dishes prepared with fresh, authentic ingredients in a warm atmosphere.
        </p>
        <button className="bg-white text-orange-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100">
          Make Reservation
        </button>
      </div>
    </section>
  ),
  content: (
    <section className="py-16">
      <div className="container mx-auto px-4">
        <h3 className="text-3xl font-bold text-center mb-12">Popular Dishes</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {[
            { title: "Pasta Carbonara", price: "$24", description: "Traditional Roman pasta with eggs and pancetta", icon: "🍝" },
            { title: "Margherita Pizza", price: "$18", description: "Classic pizza with fresh basil and mozzarella", icon: "🍕" },
            { title: "Tiramisu", price: "$12", description: "Traditional Italian dessert with coffee and mascarpone", icon: "🍰" }
          ].map((dish, index) => (
            <div key={index} className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow">
              <div className="h-32 bg-gradient-to-r from-orange-500 to-red-600 flex items-center justify-center text-4xl text-white">
                {dish.icon}
              </div>
              <div className="p-6">
                <h4 className="text-xl font-semibold mb-2">{dish.title}</h4>
                <p className="text-gray-600 mb-4">{dish.description}</p>
                <div className="flex items-center justify-between">
                  <span className="text-2xl font-bold text-orange-600">{dish.price}</span>
                  <button className="bg-orange-600 text-white px-4 py-2 rounded hover:bg-orange-700">
                    Order Now
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
};

export const travelConfig: ExampleConfig = {
  backgroundColor: "cyan-50",
  chatButtonColor: "cyan-600",
  chatButtonIcon: "✈️",
  header: (
    <header className="bg-white shadow-sm">
      <div className="container mx-auto px-4 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Plane className="w-8 h-8 text-cyan-600" />
            <h1 className="text-2xl font-bold text-gray-800">WanderLust Travel</h1>
          </div>
          <nav className="flex space-x-6">
            <a href="#" className="text-gray-600 hover:text-cyan-600">Destinations</a>
            <a href="#" className="text-gray-600 hover:text-cyan-600">Packages</a>
            <a href="#" className="text-gray-600 hover:text-cyan-600">Hotels</a>
            <button className="bg-cyan-600 text-white px-4 py-2 rounded-lg hover:bg-cyan-700">
              Book Trip
            </button>
          </nav>
        </div>
      </div>
    </header>
  ),
  hero: (
    <section className="bg-gradient-to-br from-cyan-600 to-blue-600 text-white py-20">
      <div className="container mx-auto px-4 text-center">
        <h2 className="text-5xl font-bold mb-6">Explore the World</h2>
        <p className="text-xl mb-8 max-w-3xl mx-auto">
          Discover amazing destinations and create unforgettable memories with our curated travel experiences.
        </p>
        <button className="bg-white text-cyan-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100">
          Plan Your Journey
        </button>
      </div>
    </section>
  ),
  content: (
    <section className="py-16">
      <div className="container mx-auto px-4">
        <h3 className="text-3xl font-bold text-center mb-12">Popular Destinations</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {[
            { title: "Tropical Paradise", price: "From $1,299", description: "7 days in Bali with beach resorts", icon: "🏝️" },
            { title: "European Adventure", price: "From $2,199", description: "10 days across 5 European cities", icon: "🏰" },
            { title: "Safari Experience", price: "From $3,499", description: "African wildlife safari adventure", icon: "🦁" }
          ].map((destination, index) => (
            <div key={index} className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow">
              <div className="h-32 bg-gradient-to-r from-cyan-500 to-blue-600 flex items-center justify-center text-4xl text-white">
                {destination.icon}
              </div>
              <div className="p-6">
                <h4 className="text-xl font-semibold mb-2">{destination.title}</h4>
                <p className="text-gray-600 mb-4">{destination.description}</p>
                <div className="flex items-center justify-between">
                  <span className="text-2xl font-bold text-cyan-600">{destination.price}</span>
                  <button className="bg-cyan-600 text-white px-4 py-2 rounded hover:bg-cyan-700">
                    Learn More
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
};